import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { colors3 } from "../../colors/color";
import CustomizeTootip from "../../components/CustomizeTootip";
import CustomizeLegend from "../../components/CustomizeLegend";
import Printer2 from "../../components/Printer2";
import GraphLoader from "../../components/GraphLoader";
import { Grid } from "@mui/material";
import { forecastingTransformerTemperatureAction } from "../../actions/predictiveAction";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

const PredictiveTransformerTemperature = ({ selectedLebel, databaseStr }) => {
  const {
    forecastedTransformerTemperature,
    transformerTemperatureLoading: loading,
  } = useSelector((state) => state.forecasting);

  const graphRef = useRef(null);
  const [showBarData, setShowBarData] = useState({
    transformerTemperature: true,
    forecastedTransformerTemperature: true,
  });

  // New state for filter
  const [selectedFilter, setSelectedFilter] = useState("all");

  const minMaxRangeAndGraphData = useMemo(() => {
    if (forecastedTransformerTemperature && forecastedTransformerTemperature?.length > 0) {
      // Create the base graph data with difference calculation
      const baseGraphData = forecastedTransformerTemperature?.reduce((acc, curr, index, array) => {
        if (curr["Transformer Oil Temp"] || curr["Forecasted Transformer Oil Temp"]) {
          let monthOverMonthChange = null;
          let monthOverMonthPercentage = null;
          
          // Calculate difference from previous month
          if (index > 0) {
            const currentTemperature = curr["Transformer Oil Temp"] || curr["Forecasted Transformer Oil Temp"];
            const prevTemperature = array[index - 1]["Transformer Oil Temp"] || array[index - 1]["Forecasted Transformer Oil Temp"];
            
            if (currentTemperature != null && prevTemperature != null) {
              monthOverMonthChange = currentTemperature - prevTemperature;
              monthOverMonthPercentage = prevTemperature !== 0 
                ? ((monthOverMonthChange / prevTemperature) * 100).toFixed(2)
                : 0;
            }
          }

          acc.push({
            Date: curr["Date"],
            "Transformer Oil Temp": curr["Transformer Oil Temp"],
            predicted: curr["predicted"] || false,
            actual_gii: curr["actual_gii"],
            "Forecasted Transformer Oil Temp": curr["Forecasted Transformer Oil Temp"],
            // Add difference data for tooltip
            "Month Over Month Change": monthOverMonthChange,
            "Month Over Month Percentage": monthOverMonthPercentage,
          });
        }
        return acc;
      }, []) || [];

      // Apply filter based on selection
      const filteredGraphData = baseGraphData.filter((item) => {
        const transformerTemperature = item["Transformer Oil Temp"];
        const forecastedTransformerTemperature = item["Forecasted Transformer Oil Temp"];

        switch (selectedFilter) {
          case "gt_25":
            return transformerTemperature > 25 || forecastedTransformerTemperature > 25;
          case "gt_5":
            return transformerTemperature > 5 || forecastedTransformerTemperature > 5;
          case "all":
          default:
            return true;
        }
      });

      // Calculate min/max based on filtered data
      const validLossValues = filteredGraphData
        .map((item) => item["Transformer Oil Temp"])
        .filter((value) => value != null);

      return {
        graphData: filteredGraphData,
        min:
          validLossValues.length > 0
            ? Math.floor(Math.min(...validLossValues))
            : 0,
        max:
          validLossValues.length > 0
            ? Math.ceil(Math.max(...validLossValues))
            : 1,
        totalCount: baseGraphData.length,
        filteredCount: filteredGraphData.length,
      };
    }
    return {};
  }, [forecastedTransformerTemperature, selectedFilter]);

  console.log("minMaxRangeAndGraphData", minMaxRangeAndGraphData);

  // Filter options configuration
  const filterOptions = [
    { value: "all", label: "Show All" },
    { value: "gt_25", label: "Transformer Oil Temp > 25°C" },
    { value: "gt_5", label: "Transformer Oil Temp > 5°C" },
  ];

  // Custom tooltip component to show difference with colors
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const monthOverMonthChange = data["Month Over Month Change"];
      const monthOverMonthPercentage = data["Month Over Month Percentage"];

      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "5px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          }}
        >
          <p style={{ margin: "0 0 5px 0", fontWeight: "bold" }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <p
              key={index}
              style={{
                margin: "2px 0",
                color: entry.color,
                fontSize: "0.8rem",
              }}
            >
              {entry.name}: <strong>{entry.value?.toFixed(2)}°C</strong>
            </p>
          ))}
          {monthOverMonthChange !== null && monthOverMonthPercentage !== null && (
            <p
              style={{
                margin: "5px 0 0 0",
                padding: "3px 6px",
                borderRadius: "3px",
                backgroundColor: monthOverMonthChange >= 0 ? "#f6ffed" : "#fff2f0",
                border: `1px solid ${monthOverMonthChange >= 0 ? "#b7eb8f" : "#ffccc7"}`,
                fontSize: "0.75rem",
                fontWeight: "bold",
                color: monthOverMonthChange >= 0 ? "#389e0d" : "#a8071a",
              }}
            >
              vs Previous Month: 
              <span style={{ marginLeft: "5px" }}>
                {monthOverMonthChange >= 0 ? "+" : ""}{monthOverMonthChange?.toFixed(2)}°C 
                ({monthOverMonthChange >= 0 ? "+" : ""}{monthOverMonthPercentage}%)
              </span>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const dispatch = useDispatch();
  const location = useLocation();
  
  useEffect(() => {
    if (selectedLebel?.endTimestamp && databaseStr) {
      dispatch(forecastingTransformerTemperatureAction(selectedLebel, databaseStr));
    }
  }, [
    dispatch,
    selectedLebel?.endTimestamp,
    selectedLebel?.startTimestamp,
    databaseStr,
  ]);

  useEffect(() => {
    if (databaseStr) {
      dispatch(forecastingTransformerTemperatureAction({ dateRangeType: "month" }, databaseStr));
    }
  }, [location.pathname, databaseStr, dispatch]);

  return (
    <>
      <Grid container xl={12} textAlign={"center"}>
        <Grid container xl={12}>
          <Grid
            item
            xl={11.8}
            lg={11.8}
            md={11.8}
            sm={11.8}
            xs={11.8}
            style={{
              background:
                "linear-gradient(to bottom, rgb(3, 99, 125),rgb(4, 128, 162),rgb(4, 135, 172))",
              margin: "0 auto 0.5vmax",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0.5vmax",
            }}
          >
            <Grid
              item
              xs={12}
              style={{
                background: "white",
                padding: "0.5vmax 0.5vmax",
                position: "relative",
                overflow: "auto",
              }}
            >
              <div
                style={{
                  padding: "0.5rem 1.5rem",
                  position: "sticky",
                  left: "0",
                  right: "0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  textAlign: "center",
                  zIndex: "1",
                  backgroundColor: "white",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                {/* Left side - Filter */}
                {/* <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <label
                      htmlFor="transformer-temperature-filter"
                      style={{
                        fontSize: "0.9rem",
                        fontWeight: 500,
                        color: "#333",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      Filter:
                    </label>
                    <select
                      id="transformer-temperature-filter"
                      value={selectedFilter}
                      onChange={(e) => setSelectedFilter(e.target.value)}
                      style={{
                        padding: "0.4rem 0.8rem",
                        border: "1px solid #d9d9d9",
                        borderRadius: "4px",
                        fontSize: "0.8rem",
                        backgroundColor: "white",
                        cursor: "pointer",
                        minWidth: "160px",
                      }}
                    >
                      {filterOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>

                    {selectedFilter !== "all" && minMaxRangeAndGraphData.totalCount > 0 && (
                      <span
                        style={{
                          fontSize: "0.75rem",
                          backgroundColor: "#fff2e8",
                          color: "#fa541c",
                          padding: "2px 8px",
                          borderRadius: "12px",
                          border: "1px solid #ffbb96",
                          fontWeight: 500,
                        }}
                      >
                        {minMaxRangeAndGraphData.filteredCount} of{" "}
                        {minMaxRangeAndGraphData.totalCount} months
                      </span>
                    )}
                  </div>
                </div> */}

                {/* Center - Title */}
                <h5 style={{ margin: 0, flex: 1, textAlign: "center" }}>
                  Forecasted Transformer Oil Temp (°C)
                  {selectedFilter !== "all" && (
                    <span
                      style={{
                        fontSize: "0.7rem",
                        color: "#fa541c",
                        marginLeft: "8px",
                        fontWeight: "normal",
                      }}
                    >
                      (
                      {filterOptions
                        .find((opt) => opt.value === selectedFilter)
                        ?.label.replace("Show All", "")
                        .replace("Filter: ", "")}
                      )
                    </span>
                  )}
                </h5>

                {/* Right side - Printer */}
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Printer2
                    dataSetDetail={{
                      data: minMaxRangeAndGraphData.graphData,
                      name: "Forecasted Transformer Oil Temp",
                      ref: graphRef,
                      refCurrent: graphRef.current?.container,
                    }}
                  />
                </div>
              </div>

              <div
                style={{
                  minWidth: "100%",
                  width:
                    forecastedTransformerTemperature?.length > 0
                      ? 2 * forecastedTransformerTemperature.length * 55 + "px"
                      : "100%",
                }}
                className="inverter-efficiency-responsive-container"
              >
                <ResponsiveContainer>
                  {loading ? (
                    <div
                      style={{
                        position: "absolute",
                        top: "0",
                        bottom: "0",
                        right: "0",
                        left: "0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <GraphLoader />
                    </div>
                  ) : minMaxRangeAndGraphData.graphData?.length > 0 ? (
                    <ComposedChart
                      barCategoryGap={"20%"}
                      ref={graphRef}
                      margin={{
                        top: 15,
                        bottom: 5,
                        left: 5,
                        right: 5,
                      }}
                      data={minMaxRangeAndGraphData.graphData || []}
                    >
                      <XAxis
                        fontSize={"0.8rem"}
                        fontWeight={600}
                        dataKey="Date"
                        tickLine={false}
                        axisLine={false}
                        tickMargin={5}
                      />
                      <CartesianGrid stroke="grey" strokeWidth={0.3} />

                      <YAxis
                        type="number"
                        fontSize={"0.8rem"}
                        fontWeight={600}
                        tickLine={false}
                        tickMargin={5}
                        tickCount={6}
                      />

                      <Legend
                        content={
                          <CustomizeLegend
                            active={false}
                            payload={[]}
                            label={""}
                            LegendValues={[
                              "Transformer Oil Temp",
                              "Forecasted Transformer Oil Temp",
                            ]}
                            data={showBarData}
                            setData={setShowBarData}
                            styles={{ fontSize: "0.8rem" }}
                          />
                        }
                      />
                      
                      {/* Use custom tooltip */}
                      <Tooltip
                        cursor={{ fill: "none" }}
                        content={<CustomTooltip />}
                        contentStyle={{ fontSize: "0.7rem" }}
                      />

                      <Bar
                        hide={!showBarData.transformerTemperature}
                        dataKey="Transformer Oil Temp"
                        fill={colors3[0]}
                        onTransitionEnd={"1s all"}
                        maxBarSize={30}
                      >
                        <LabelList
                          style={{ zIndex: 6 }}
                          dataKey="Transformer Oil Temp"
                          position="top"
                          fontSize={"0.8rem"}
                          fontWeight={600}
                          fontFamily="Cambria, Cochin, Georgia, Times, 'Times New Roman', serif;"
                        />
                      </Bar>

                      <Bar
                        hide={!showBarData.forecastedTransformerTemperature}
                        dataKey="Forecasted Transformer Oil Temp"
                        fill="red"
                        onTransitionEnd={"1s all"}
                        maxBarSize={30}
                      >
                        <LabelList
                          style={{ zIndex: 6 }}
                          dataKey="Forecasted Transformer Oil Temp"
                          position="top"
                          fontSize={"0.8rem"}
                          fontWeight={600}
                          fontFamily="Cambria, Cochin, Georgia, Times, 'Times New Roman', serif;"
                        />
                      </Bar>
                    </ComposedChart>
                  ) : (
                    <div
                      style={{
                        height: "100%",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: "1rem",
                      }}
                    >
                      <h3>
                        {selectedFilter !== "all"
                          ? "No data matches the filter criteria"
                          : "Data Not Available"}
                      </h3>
                      {selectedFilter !== "all" && (
                        <button
                          onClick={() => setSelectedFilter("all")}
                          style={{
                            padding: "0.5rem 1rem",
                            backgroundColor: "#1890ff",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "0.9rem",
                          }}
                        >
                          Show All Data
                        </button>
                      )}
                    </div>
                  )}
                </ResponsiveContainer>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default PredictiveTransformerTemperature;